import { useMemo, useState } from "react";
import type {
  Lead,
  LeadRole,
  Opportunity,
  OpportunityStakeholder,
  StakeholderAttitude,
  StakeholderInfluence,
} from "@sales-ops-amplify/shared";
import { useLeads } from "@/hooks/useCompanies";
import { useUpdateOpportunity } from "@/hooks/useOpportunities";
import { cn } from "@/cn";

const ROLES: LeadRole[] = [
  "buyer",
  "champion",
  "influencer",
  "procurement",
  "referrer",
  "other",
];

const ATTITUDES: StakeholderAttitude[] = [
  "champion",
  "supporter",
  "neutral",
  "skeptic",
  "blocker",
];

const INFLUENCES: StakeholderInfluence[] = ["high", "medium", "low"];

interface BuyingCommitteeProps {
  opportunity: Opportunity;
}

export function BuyingCommittee({ opportunity }: BuyingCommitteeProps) {
  const updateJob = useUpdateOpportunity(opportunity.id);
  const { data: contactsData } = useLeads({ opportunityId: opportunity.id });
  const { data: allContactsData } = useLeads(
    opportunity.accountId
      ? { accountId: opportunity.accountId }
      : undefined,
  );

  const linkedContacts = contactsData?.people ?? [];
  const companyContacts = allContactsData?.people ?? [];
  const pickerContacts = useMemo(() => {
    const byId = new Map<string, Lead>();
    for (const c of [...companyContacts, ...linkedContacts]) {
      byId.set(c.id, c);
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [companyContacts, linkedContacts]);

  const committee = opportunity.committee ?? [];

  const derivedFallback = useMemo(() => {
    if (committee.length > 0) return [];
    return linkedContacts.map(
      (c): OpportunityStakeholder => ({
        leadId: c.id,
        name: c.name,
        title: c.title,
        role: c.role ?? "other",
      }),
    );
  }, [committee, linkedContacts]);

  const displayed = committee.length > 0 ? committee : derivedFallback;
  const isDerived = committee.length === 0 && derivedFallback.length > 0;

  const [leadId, setContactId] = useState("");
  const [role, setRole] = useState<LeadRole>("buyer");
  const [attitude, setAttitude] = useState<StakeholderAttitude>("neutral");
  const [influence, setInfluence] = useState<StakeholderInfluence>("medium");
  const [isPrimary, setIsPrimary] = useState(false);

  const contactById = useMemo(() => {
    const map = new Map<string, Lead>();
    for (const c of pickerContacts) map.set(c.id, c);
    return map;
  }, [pickerContacts]);

  function saveCommittee(next: OpportunityStakeholder[]) {
    updateJob.mutate({ committee: next } as Partial<Opportunity> as never);
  }

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!leadId) return;
    const contact = contactById.get(leadId);
    const nextEntry: OpportunityStakeholder = {
      leadId,
      name: contact?.name,
      title: contact?.title,
      role,
      attitude,
      influence,
      isPrimary,
    };

    const base =
      committee.length > 0
        ? [...committee]
        : derivedFallback.map((s) => ({ ...s }));

    let next = isPrimary
      ? base.map((s) => ({ ...s, isPrimary: false }))
      : base;

    const existingIdx = next.findIndex((s) => s.leadId === leadId);
    if (existingIdx >= 0) {
      next[existingIdx] = { ...next[existingIdx], ...nextEntry };
    } else {
      next.push(nextEntry);
    }

    saveCommittee(next);
    setContactId("");
    setIsPrimary(false);
  }

  function handleRemove(id: string) {
    saveCommittee(committee.filter((s) => s.leadId !== id));
  }

  function promoteDerived() {
    if (derivedFallback.length === 0) return;
    saveCommittee(derivedFallback);
  }

  return (
    <div className="mb-6 rounded-lg border border-border bg-card p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Contacts on deal
        </h3>
        {isDerived && (
          <button
            type="button"
            onClick={promoteDerived}
            disabled={updateJob.isPending}
            className="text-xs text-brand hover:underline disabled:opacity-50"
          >
            Save as committee
          </button>
        )}
      </div>

      {isDerived && (
        <p className="mb-3 text-xs text-muted-foreground">
          Showing contacts linked to this deal. Add a stakeholder or save as committee
          to persist politics (attitude / influence).
        </p>
      )}

      {displayed.length === 0 ? (
        <p className="mb-4 text-sm text-muted-foreground">
          No stakeholders yet. Pick a contact below.
        </p>
      ) : (
        <ul className="mb-4 space-y-2">
          {displayed.map((s) => (
            <li
              key={s.leadId}
              className="flex items-start justify-between gap-2 rounded-md border border-border bg-bg px-3 py-2 text-sm"
            >
              <div>
                <p className="font-medium text-foreground">
                  {s.name ?? contactById.get(s.leadId)?.name ?? s.leadId}
                  {s.isPrimary && (
                    <span className="ml-2 rounded bg-brand/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brand">
                      Primary
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {[
                    s.role,
                    s.title,
                    s.attitude,
                    s.influence ? `influence:${s.influence}` : undefined,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              {!isDerived && (
                <button
                  type="button"
                  onClick={() => handleRemove(s.leadId)}
                  className="shrink-0 text-xs text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={handleAdd}
        className="space-y-3 rounded-md border border-border bg-bg p-3"
      >
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Add stakeholder
        </h4>
        <div className="grid gap-2 sm:grid-cols-2">
          <select
            value={leadId}
            onChange={(e) => setContactId(e.target.value)}
            required
            className="rounded-md border border-border bg-card px-3 py-2 text-sm sm:col-span-2"
          >
            <option value="">Select contact…</option>
            {pickerContacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.title ? ` — ${c.title}` : ""}
              </option>
            ))}
          </select>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as LeadRole)}
            className="rounded-md border border-border bg-card px-3 py-2 text-sm capitalize"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            value={attitude}
            onChange={(e) => setAttitude(e.target.value as StakeholderAttitude)}
            className="rounded-md border border-border bg-card px-3 py-2 text-sm capitalize"
          >
            {ATTITUDES.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <select
            value={influence}
            onChange={(e) => setInfluence(e.target.value as StakeholderInfluence)}
            className="rounded-md border border-border bg-card px-3 py-2 text-sm capitalize"
          >
            {INFLUENCES.map((i) => (
              <option key={i} value={i}>
                {i} influence
              </option>
            ))}
          </select>
          <label
            className={cn(
              "flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm",
            )}
          >
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
            />
            Primary contact
          </label>
        </div>
        <button
          type="submit"
          disabled={!leadId || updateJob.isPending || pickerContacts.length === 0}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
        >
          {updateJob.isPending ? "Saving…" : "Add to committee"}
        </button>
        {pickerContacts.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Add a contact for this company first, then attach them to the committee.
          </p>
        )}
      </form>
    </div>
  );
}
